import React from "react";
import Menu from "../Menu";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useState, useEffect } from "react";
import SuccessMessage from "../../components/successMessage";
import FailedMessage from "../../components/FailedMessage";
import useApiUrls from "../../useApiUrls";

const UpdateSerialnoMQTTSetting = () => {
  const navigate = useNavigate();
  const { id, deviceId } = useParams(); // Get the MQTTSetting ID from the URL
  const { getUrlByOperation, error: urlError } = useApiUrls();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [originalData, setOriginalData] = useState(null);

  // Updated state to hold all required form data
  const [formData, setFormData] = useState({
    id: id,
    remark: "",
    active: true,
    lockkey: true,
    deviceid: deviceId,
    json: "{}"
  });

  // State to hold API response or error
  const [message, setMessage] = useState("");

  // Fetch the existing data for the MQTTSetting
  useEffect(() => {
    if (id) {
      const fetchMQTTSettingData = async () => {
        const token = getCookie("token");
        const userid = getCookie("userid");

        if (!token || !userid) {
          console.error("Missing token or userId in cookies");
          setMessage("Authentication error. Please log in again.");
          return;
        }

        try {
          const response = await axios.post(
            'https://api.dqr-display-tms.bonrix.in/api/DeviceJsonSettingOperation/Search',
            { id: parseInt(id, 10) },
            {
              headers: {
                "X-Authorization": token,
                UserId: userid,
                "Content-Type": "application/json",
              },
            }
          );

          if (response.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
            const settingData = response.data.data[0];
            setOriginalData(settingData);

            // Try to parse JSON if it's a string
            let jsonData = settingData.json;
            if (typeof jsonData === 'string') {
              try {
                // If it's valid JSON, leave it as a string for display purposes
                JSON.parse(jsonData);
                // No action needed, keep as string for the form
              } catch (e) {
                // If not valid JSON, default to empty object
                jsonData = "{}";
              }
            } else if (jsonData && typeof jsonData === 'object') {
              // If it's already an object, stringify it for the form
              jsonData = JSON.stringify(jsonData, null, 2);
            } else {
              // Default to empty object
              jsonData = "{}";
            }

            // Map API response to form data
            setFormData({
              id: settingData.id,
              remark: settingData.remark || "",
              active: settingData.active,
              lockkey: settingData.lockkey,
              deviceid: settingData.deviceid ? settingData.deviceid.toString() : "1",
              json: jsonData
            });
          } else {
            setMessage("No setting found with the specified ID.");
          }
        } catch (error) {
          console.error("Error fetching Device JSON Setting data:", error);
          setMessage(`Failed to fetch Device JSON Setting data: ${error.response?.data?.errormsg || error.message}`);
        }
      };

      fetchMQTTSettingData();
    }
  }, [id]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: name === "active" ? value === "true" : value,
    }));
  };

  // Handle JSON input with validation
  const handleJsonChange = (e) => {
    const value = e.target.value;
    setFormData((prevData) => ({
      ...prevData,
      json: value,
    }));
  };

  // Function to check if JSON is valid
  const isJsonValid = (jsonString) => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch (error) {
      return false;
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const token = getCookie("token");
    const userid = getCookie("userid");

    if (!token || !userid) {
      console.error("Missing token or userId in cookies");
      setMessage("Authentication error. Please log in again.");
      setIsSubmitting(false);
      return;
    }

    try {
      // Fix 1: Convert id to number
      const idNum = parseInt(formData.id, 10);
      
      // Fix 2: Convert deviceid to number
      const deviceIdNum = parseInt(formData.deviceid, 10);
      
      // Fix 3: Parse the JSON string if it's valid JSON, or use empty object as fallback
      let jsonData;
      try {
        jsonData = isJsonValid(formData.json) ? JSON.parse(formData.json) : {};
      } catch (e) {
        jsonData = {};
      }
      
      // Prepare the data to be sent with fixes applied
      const updateData = {
        id: isNaN(idNum) ? 0 : idNum,
        remark: formData.remark.trim(), // Trim whitespace from remark
        active: formData.active === "true" || formData.active === true,
        lockkey: formData.lockkey === "true" || formData.lockkey === true,
        deviceid: isNaN(deviceIdNum) ? 1 : deviceIdNum, // Use number, default to 1 if parsing fails
        json: jsonData // Send as parsed object instead of string
      };
      
      console.log("Sending data to API:", JSON.stringify(updateData, null, 2));
      
      // Fix 4: Change Content-Type to application/json
      const response = await axios.post(
        'https://api.dqr-display-tms.bonrix.in/api/DeviceJsonSettingOperation/Update',
        updateData,
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            "Content-Type": "application/json", // Changed from application/json-patch+json
          },
        }
      );

      // Check if update was successful
      if (response.data && response.data.isresponse && response.data.responsestatus === "SUCCESS") {
        setMessage("Device JSON Setting updated successfully!");

        // Set a delay before navigating
        setTimeout(() => {
          navigate("/mqttSetting");
        }, 3000);
      } else {
        setMessage(`Failed to update Device JSON Setting: ${response.data?.errormsg || "Unknown error"}`);
        console.error("API Response:", response.data);
      }
    } catch (error) {
      console.error("Error updating Device JSON Setting:", error);
      console.error("Error details:", error.response?.data);
      setMessage(`Failed to update Device JSON Setting: ${error.response?.data?.errormsg || error.message || "Server error"}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Menu />
      <div className="wrapper">
        <div className="content-wrapper">
          <section className="content-header">
            <div className="container-fluid">
              <div className="row mb-2">
                <div className="col-sm-6">
                  <h1>Update Device JSON Setting</h1>
                </div>
              </div>
            </div>
          </section>
          <section className="content">
            <div className="container-fluid">
              <div className="row">
                <div className="col-md-12">
                  <div className="card card-info">
                    <div className="card-header">
                      <h3 className="card-title">Update Device JSON Setting</h3>
                    </div>
                    <div className="card-body">
                      <form id="add-items" onSubmit={handleSubmit}>
                        <div className="row">
                          <div className="col-sm-6">
                            <div className="form-group">
                              <label>Description</label>
                              <input
                                type="text"
                                className="form-control"
                                value={formData.remark}
                                onChange={handleChange}
                                placeholder="Enter description..."
                                name="remark"
                              />
                            </div>
                          </div>
                     
                           <div className="col-sm-6">
                            <div className="form-group">
                              <label>Active</label>
                              <select
                                className="form-control"
                                name="active"
                                value={formData.active.toString()}
                                onChange={handleChange}
                              >
                                <option value="true">Active</option>
                                <option value="false">Deactive</option>
                              </select>
                            </div>
                          </div>
                        </div>

                        <div className="row">
                           <div className="col-sm-6">
                            <div className="form-group">
                              <label>	Lockkey</label>
                              <select
                                className="form-control"
                                name="lockkey"
                                value={formData.lockkey.toString()}
                                onChange={handleChange}
                              >
                                <option value="true">Active</option>
                                <option value="false">Deactive</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      
                        <div className="row">
                          <div className="col-sm-12">
                            <div className="form-group">
                              <label>JSON Configuration</label>
                              <textarea
                                className={`form-control ${!isJsonValid(formData.json) && formData.json !== "{}" ? "is-invalid" : ""}`}
                                value={formData.json}
                                onChange={handleJsonChange}
                                placeholder="Enter JSON configuration..."
                                name="json"
                                rows="4"
                              ></textarea>
                              {!isJsonValid(formData.json) && formData.json !== "{}" && (
                                <div className="invalid-feedback">
                                  Please enter valid JSON format
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="row mt-3">
                          <div className="col-sm-12">
                            <div className="form-group">
                            </div>
                          </div>
                        </div>

                        <div className="form-footer mt-3">
                          <button
                            type="submit"
                            id="add-sub"
                            className="btn btn-info"
                            disabled={!isJsonValid(formData.json) || isSubmitting}
                          >
                            {isSubmitting ? "Updating..." : "Update"}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {message === "Device JSON Setting updated successfully!" ? (
        <SuccessMessage message={message} />
      ) : message.startsWith("Failed to update Device JSON Setting") ? (
        <FailedMessage message={message} />
      ) : message ? (
        <div className="alert alert-info alert-dismissible fade show" role="alert">
          {message}
          <button type="button" className="close" data-dismiss="alert" aria-label="Close" onClick={() => setMessage("")}>
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      ) : null}
    </>
  );
};

export default UpdateSerialnoMQTTSetting;

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}